import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, GitCompareArrows } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { api } from '../lib/api';
import { useSeo } from '../lib/seo';

type AltSummary = { slug: string; competitor: string; description: string };

export function Alternatives() {
  useSeo({
    title: 'Alternativas a Metricool, Buffer, Hootsuite y más · Zettapost',
    description:
      'Compara Zettapost con Metricool, Buffer, Hootsuite, Later, Publer y SocialBee. La alternativa más simple, con IA y en español para programar y publicar en redes.',
    canonical: 'https://zettapost.com/alternativas',
  });

  const { data } = useQuery({
    queryKey: ['alternatives'],
    queryFn: () => api<{ alternatives: AltSummary[] }>('/api/alternatives'),
  });
  const alternatives = data?.alternatives ?? [];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-50/40 to-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-14 lg:py-20 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
            <GitCompareArrows size={13} />
            Comparativas
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Alternativas a las herramientas
            <br />
            de redes sociales más conocidas
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            ¿Buscas algo más simple, con IA y en español? Compara Zettapost cara a cara con las
            herramientas que ya conoces.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid sm:grid-cols-2 gap-4">
          {alternatives.map((a) => (
            <Link
              key={a.slug}
              to={`/alternativas/${a.slug}`}
              className="group rounded-2xl border border-slate-200 p-5 hover:border-brand-300 hover:shadow-md transition flex flex-col"
            >
              <h2 className="font-bold text-lg text-slate-900 group-hover:text-brand-700 transition">
                Zettapost vs {a.competitor}
              </h2>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed flex-1">{a.description}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600">
                Ver comparativa
                <ArrowRight size={15} className="transition group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
